import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useOrganizationsCatalog } from "@/hooks/useOrganizations";
import {
  PROGRAM_STATUSES,
  Program,
  ProgramPayload,
  ProgramStatus,
} from "@/domain/programs.interface";
import programValidationSchema, { ProgramFormData } from "./validation.schema";

type ProgramModalProps = {
  open: boolean;
  onClose: () => void;
  program?: Program;
  onSave: (data: ProgramPayload) => Promise<void>;
  isPending: boolean;
};

const DEFAULT_VALUES: ProgramFormData = {
  name: "",
  status: "PENDING",
  eventName: "",
  orgUnitId: [],
  requiresCaseNumber: false,
  maxParticipants: undefined,
  starDate: "",
  endDate: "",
};

const toDateInput = (iso?: string) =>
  iso ? dayjs(iso).format("YYYY-MM-DD") : "";

const toIso = (value: string) =>
  value ? dayjs(value).toISOString() : undefined;

const ProgramModal: FC<ProgramModalProps> = ({
  open,
  onClose,
  program,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const isEdit = !!program;

  const { organizationsCatalog, isLoadingOrganizationsCatalog } =
    useOrganizationsCatalog(open);

  const orgUnitOptions = useMemo(
    () =>
      (organizationsCatalog || []).map((org) => ({
        key: org._id,
        value: org.name,
      })),
    [organizationsCatalog]
  );

  const statusOptions = useMemo(
    () =>
      PROGRAM_STATUSES.map((status) => ({
        key: status,
        value: status.charAt(0) + status.slice(1).toLowerCase(),
      })),
    []
  );

  const methods = useForm<ProgramFormData>({
    resolver: yupResolver(programValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (program) {
      reset({
        name: program.name,
        status: program.status,
        eventName: program.eventName || "",
        orgUnitId: program.orgUnitId || [],
        requiresCaseNumber: program.parameters?.requiresCaseNumber ?? false,
        maxParticipants: program.parameters?.maxParticipants,
        starDate: toDateInput(program.parameters?.starDate),
        endDate: toDateInput(program.parameters?.endDate),
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [program, reset]);

  const onSubmit = async (data: ProgramFormData) => {
    const currentUserId = userInfo?._id as string;

    const hasParameters =
      !!data.starDate ||
      !!data.endDate ||
      data.maxParticipants !== undefined ||
      data.requiresCaseNumber;

    const payload: ProgramPayload = {
      name: data.name.trim(),
      status: data.status as ProgramStatus,
      orgUnitId: (data.orgUnitId || []) as string[],
      ...(data.eventName ? { eventName: data.eventName.trim() } : {}),
      ...(hasParameters
        ? {
            parameters: {
              requiresCaseNumber: !!data.requiresCaseNumber,
              maxParticipants: data.maxParticipants ?? 0,
              starDate: toIso(data.starDate) ?? "",
              ...(data.endDate ? { endDate: toIso(data.endDate) } : {}),
            },
          }
        : {}),
      ...(isEdit ? { updatedBy: currentUserId } : { createdBy: currentUserId }),
    };
    await onSave(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit Program" : "New program"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 560,
        maxHeight: 740,
        maxWidth: 820,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="program-name"
                      label="Name"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.name}
                      helperText={errors.name ? errors.name.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={statusOptions}
                      value={field.value as string}
                      label="Status"
                      id="program-status"
                      required
                      error={!!errors.status}
                      errorMessage={errors.status?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="eventName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="program-event-name"
                      label="Event name"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.eventName}
                      helperText={
                        errors.eventName ? errors.eventName.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="orgUnitId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) =>
                        field.onChange(value as string[])
                      }
                      data={orgUnitOptions}
                      value={(field.value as string[]) || []}
                      label="Org units"
                      id="program-org-units"
                      required
                      multiple
                      isLoading={isLoadingOrganizationsCatalog}
                      error={!!errors.orgUnitId}
                      errorMessage={
                        (errors.orgUnitId as { message?: string })?.message ||
                        ""
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="titleSmall">
                  Parameters (optional)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="maxParticipants"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="program-max-participants"
                      label="Max participants"
                      type="number"
                      variant="outlined"
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === ""
                            ? undefined
                            : Number(e.target.value)
                        )
                      }
                      error={!!errors.maxParticipants}
                      helperText={
                        errors.maxParticipants
                          ? errors.maxParticipants.message
                          : null
                      }
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="starDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="program-start-date"
                      label="Start date"
                      type="date"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.starDate}
                      helperText={
                        errors.starDate ? errors.starDate.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="program-end-date"
                      label="End date"
                      type="date"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.endDate}
                      helperText={
                        errors.endDate ? errors.endDate.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="requiresCaseNumber"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Requires case number"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default ProgramModal;
